import React, { useEffect, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import useSWR from 'swr';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import '@toast-ui/calendar/dist/toastui-calendar.min.css';
import 'tui-date-picker/dist/tui-date-picker.css';
import 'tui-time-picker/dist/tui-time-picker.css';
import Papa from 'papaparse';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import { srTaskListFormatter } from '../../api/timesheet';
import { secondaryColors } from '../../theme/themeConfig';
import TimesheetReviewTable from './components/TimesheetReviewTable';
import TimesheetReportTable from './components/TimesheetReportTable';
import { getCurrency } from '../../utils/general';
import ViewLoader from '../../components/shared/ViewLoader';
import useNavigationStore from '../../store/zustand/navigation';
import useAuthStore from '../../store/zustand/auth';
import CustomRadio from '../../components/form/CustomRadio';
import {
  decimalsToHours,
  decimalsToHoursWithSeconds,
  formatDecimalHours,
  decimalsToHHMMDecimal,
  calculateWorkedHoursDecimal
} from '../../utils/calendarHelper';

const Timesheet: React.FC = function Timesheet() {
  const [step, setStep] = useState<number>(0);
  const [getTaskToReview, setGetTaskToReview] = useState(false);
  const [taskToReviewData, setTaskToReviewData] = useState<any>([]);
  const [getReportData, setGetReportData] = useState(false);
  const [userIndexSelect, setUserIndexSelect] = useState<number>(0);
  const [reportData, setReportData] = useState<any>({});
  const [fireUpdateTaskDecision, setFireUpdateTaskDecision] = useState(false);
  const [reportByDate, setReportByDate] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { t } = useTranslation();
  const theme = useTheme();
  const { setConfirmModal } = useNavigationStore((state) => state);
  const { state } = useLocation();
  const userState = useAuthStore((state) => state);

  const [reportRanges, setReportRanges] = useState<any>({
    fromDate: null,
    fromDateFormated: '',
    toDate: null,
    toDateFormated: ''
  });

  const [updateDecisionData, setUpdateDecisionData] = useState({
    actualTotal: 0,
    approvalStatus: '',
    taskIterationId: -1,
    type: ''
  });

  const {
    data: taskToReviewDataResult,
    error: getTaskToReviewDataError,
    mutate
  } = useSWR(
    getTaskToReview
      ? {
          timeFormat: userState?.timeFormat,
          url: 'api/timesheet/taskToReviewForOperator'
        }
      : null,
    api.TimesheetApi.getTaskToReviewForOperator
  );

  const {
    data: reportDataResult,
    error: getReportDataError,
    mutate: mutateReport
  } = useSWR(
    getReportData
      ? {
          fromDate: reportRanges.fromDateFormated,
          timeFormat: userState?.timeFormat,
          toDate: reportRanges.toDateFormated
        }
      : null,
    api.TimesheetApi.getTimesheetTasks
  );

  const { data: srReportDataResult } = useSWR(
    getReportData
      ? {
          fromDate: reportRanges.fromDateFormated,
          toDate: reportRanges.toDateFormated
        }
      : null,
    (params: any) =>
      api.ServiceRequestApi.getSRTimesheetTasks({
        fromDate: params.fromDate,
        toDate: params.toDate
      })
  );

  const { data: taskDecisionResult, error: taskDecisionResultError } = useSWR(
    fireUpdateTaskDecision
      ? { ...updateDecisionData, mutate, setGetTaskToReview }
      : null,
    api.TimesheetApi.updateTaskDecision
  );

  //= ================= FUNCTIONS =============================

  const handleDateChange = (newDate: any, opt: number) => {
    if (newDate !== null && `${newDate?.$y}` !== 'NaN') {
      switch (opt) {
        case 5: {
          setReportRanges({
            ...reportRanges,
            fromDate: newDate,
            fromDateFormated: newDate
              ? `${newDate.format('YYYY-MM-DD')}T00:00:00`
              : ''
          });
          break;
        }
        case 6: {
          setReportRanges({
            ...reportRanges,
            toDate: newDate,
            toDateFormated: newDate
              ? `${newDate.format('YYYY-MM-DD')}T23:59:59`
              : ''
          });
          break;
        }
        default:
          break;
      }
    }
  };

  const handleError = () => {
    setConfirmModal(
      () => {
        return null;
      },
      `${t('GENERAL.confirmButton')}`,
      () => {},
      ``,
      <Box display="flex" flexDirection="column" pl={8} pr={8}>
        <Box p={2}>
          <i
            className="custom-icon icon-info"
            style={{
              color: theme.palette.error.main,
              fontSize: 48
            }}
          />
        </Box>
        <Typography variant="primary">{`${t('TIMESHEET.error')}`}</Typography>
      </Box>
    );
  };

  //= ================= USE_EFFECTS =============================

  useEffect(() => {
    if (taskToReviewDataResult && !getTaskToReviewDataError) {
      setIsLoading(false);
      setGetTaskToReview(false);
      setTaskToReviewData(taskToReviewDataResult);
      if (state?.user) {
        const index = taskToReviewDataResult.findIndex(
          (data: any) => data.id === state.user
        );
        if (index !== -1) {
          setUserIndexSelect(index);
        }
      }
      if (!taskToReviewDataResult[userIndexSelect]) {
        setUserIndexSelect(userIndexSelect - 1);
      }
    }
  }, [taskToReviewDataResult]);

  useEffect(() => {
    if (getTaskToReviewDataError) {
      setIsLoading(false);
      handleError();
    }
  }, [getTaskToReviewDataError]);

  useEffect(() => {
    if (reportDataResult && !getReportDataError) {
      setIsLoading(false);
      setGetReportData(false);
      const srRows = srReportDataResult ?? [];
      const srFormatted = srRows.length > 0 ? srTaskListFormatter(srRows) : [];
      // Merge SR technician groups into regular ones — same tech gets combined
      const merged = [...(reportDataResult as any[])];
      srFormatted.forEach((srGroup: any) => {
        const existing = merged.find((g: any) => g.id === srGroup.id);
        if (existing) {
          existing.dataList = [...existing.dataList, ...srGroup.dataList];
          existing.totalHoursWorked += srGroup.totalHoursWorked;
          existing.technicianPaymentTotal += srGroup.technicianPaymentTotal;
        } else {
          merged.push(srGroup);
        }
      });
      setReportData(merged);
    }
  }, [reportDataResult, srReportDataResult]);

  useEffect(() => {
    if (getReportDataError) {
      setIsLoading(false);
      handleError();
    }
  }, [getReportDataError]);

  useEffect(() => {
    // Only trigger API call when date ranges change and Report tab is active
    // This handles date picker changes, not tab switches
    if (reportRanges.fromDate && reportRanges.toDate && step === 1) {
      mutateReport(null, true);
      setIsLoading(true);
      setGetReportData(true);
    }
  }, [reportRanges]);

  useEffect(() => {
    if (
      updateDecisionData?.approvalStatus !== '' &&
      updateDecisionData?.taskIterationId > 0
    ) {
      setIsLoading(true);
      setFireUpdateTaskDecision(true);
    }
  }, [updateDecisionData]);

  useEffect(() => {
    if (taskDecisionResult && !taskDecisionResultError) {
      setFireUpdateTaskDecision(false);
      mutate(null, false);
      setGetTaskToReview(true);
      setUpdateDecisionData({
        actualTotal: 0,
        approvalStatus: '',
        taskIterationId: -1,
        type: ''
      });
    }
  }, [taskDecisionResult]);

  useEffect(() => {
    if (taskDecisionResultError) {
      setIsLoading(false);
      setFireUpdateTaskDecision(false);
      handleError();
    }
  }, [taskDecisionResultError]);

  useEffect(() => {
    // Initialize with Review tab (step 0) data only
    if (step === 0) {
      setIsLoading(true);
      setGetTaskToReview(true);
    }
  }, []);

  useEffect(() => {
    // Initialize date ranges without triggering API call
    const todayDate = new Date();
    const oneMonthAgo = new Date(
      new Date().setMonth(new Date().getMonth() - 1)
    );
    // Only set the date ranges, don't trigger the API call on mount
    setReportRanges({
      fromDate: oneMonthAgo,
      fromDateFormated: oneMonthAgo ? `${oneMonthAgo.toJSON()}` : '',
      toDate: todayDate,
      toDateFormated: todayDate ? `${todayDate.toJSON()}` : ''
    });
  }, []);

  const downloadCSV = () => {
    if (reportData.length === 0) {
      return;
    }
    const csvHeaders = [
      t('TIMESHEET.technicianName'),
      t('TIMESHEET.hourlyRate'),
      t('TIMESHEET.travelTime'),
      t('TIMESHEET.totalHoursScheduled'),
      'Pause Time - Lunch',
      'Pause Time - Break',
      'Pause Time - Other',
      t('TIMESHEET.pauseTime'),
      t('TIMESHEET.totalHoursWorked'),
      'Hours Worked Decimal',
      'HH:MM (Decimals)',
      t('TIMESHEET.paymentTotal')
    ];
    const csvData = reportData?.map((item: any) => {
      const pauseTimeMs = item?.totalPauseMs || item?.values?.totalPauseMs || 0;
      const totalHoursWorked =
        parseFloat(item?.totalHoursWorked || item?.values?.totalHoursWorked) ||
        0;

      // Calculate pause times by reason
      let lunchPauseMs = 0;
      let breakPauseMs = 0;
      let otherPauseMs = 0;

      const taskList = item?.dataList || item?.tasks || [];

      if (taskList) {
        taskList.forEach((task: any) => {
          if (task?.pauseIterations) {
            task.pauseIterations.forEach((pause: any) => {
              const startTime = new Date(pause.startTime).getTime();
              const endTime = new Date(pause.endTime).getTime();
              const duration = endTime - startTime;

              if (pause.reason === 'lunch') {
                lunchPauseMs += duration;
              } else if (pause.reason === 'break') {
                breakPauseMs += duration;
              } else if (pause.reason === 'other') {
                otherPauseMs += duration;
              }
            });
          }
        });
      }

      const technicianName =
        item.technician ||
        (item.technician?.firstName && item.technician?.lastName
          ? `${item.technician.firstName} ${item.technician.lastName}`
          : 'N/A');
      const hourlyRate = item.hourlyRate || item.technician?.rate || 0;
      const totalHoursScheduled =
        item.totalHoursScheduled || item?.values?.totalHoursScheduled || 0;
      const paymentTotal =
        item.technicianPaymentTotal ||
        item?.values?.technicianPaymentTotal ||
        0;

      const allPauseIterations = taskList.flatMap(
        (task: any) => task.pauseIterations || []
      );
      const netHoursWorked = calculateWorkedHoursDecimal(
        totalHoursWorked,
        allPauseIterations
      );
      const workedHoursHHMMSS = decimalsToHoursWithSeconds(netHoursWorked);

      return [
        technicianName,
        getCurrency(hourlyRate),
        item.totalTravelTimes,
        decimalsToHours(totalHoursScheduled),
        lunchPauseMs ? decimalsToHours(lunchPauseMs / 3600000) : '00:00',
        breakPauseMs ? decimalsToHours(breakPauseMs / 3600000) : '00:00',
        otherPauseMs ? decimalsToHours(otherPauseMs / 3600000) : '00:00',
        pauseTimeMs ? decimalsToHours(pauseTimeMs / 3600000) : '00:00',
        workedHoursHHMMSS,
        formatDecimalHours(netHoursWorked, 4),
        decimalsToHHMMDecimal(netHoursWorked),
        getCurrency(paymentTotal)
      ];
    });

    const csv = Papa?.unparse({
      data: csvData,
      fields: csvHeaders
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'CSV Export File';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        padding: '32px',
        width: '100%'
      }}
    >
      {/* ── Page header ── */}
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        <Typography
          sx={{
            color: 'primary.400',
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: '-0.5px'
          }}
        >
          {t('TIMESHEET.timesheetTitle')}
        </Typography>
        <Typography sx={{ color: secondaryColors.SECONDARY, fontSize: 13 }}>
          {t('TIMESHEET.timesheetSubtitle')}
        </Typography>
      </Box>

      {/* ── Tabs + content ── */}
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px solid #E2E4EA',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        <Tabs
          aria-label="timesheet tabs"
          onChange={(_: React.SyntheticEvent, newValue: number) => {
            setStep(newValue);
            if (newValue === 0) {
              setIsLoading(true);
              mutate(null, false);
              setGetTaskToReview(true);
            }
            if (newValue === 1) {
              setIsLoading(true);
              mutateReport(null, false);
              setGetReportData(true);
            }
          }}
          sx={{
            '& .MuiTab-root': {
              '&.Mui-selected': { color: '#005DA2', fontWeight: 600 },
              color: '#7D8090',
              fontSize: '12px',
              fontWeight: 500,
              minHeight: 44,
              padding: '0 16px',
              textTransform: 'none'
            },
            '& .MuiTabs-indicator': { backgroundColor: '#005DA2', height: 2 },
            borderBottom: '1px solid #E2E4EA',
            minHeight: 44,
            px: 1
          }}
          value={step}
        >
          <Tab label="Review" />
          <Tab label="Report" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* ── Review tab ── */}
          {step === 0 && (
            <Box>
              {taskToReviewData?.length > 0 ? (
                <Box>
                  {taskToReviewData[userIndexSelect] && (
                    <TimesheetReviewTable
                      data={taskToReviewData ?? []}
                      isLoading={isLoading}
                      isTechnician={false}
                      setUpdateDecisionData={setUpdateDecisionData}
                      setUserIndexSelect={setUserIndexSelect}
                      technician={taskToReviewData[userIndexSelect] || []}
                    />
                  )}
                </Box>
              ) : (
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 8 }}
                >
                  {isLoading ? (
                    <ViewLoader />
                  ) : (
                    <Typography
                      sx={{ color: secondaryColors.SECONDARY, fontSize: 13 }}
                    >
                      {t('GENERAL.noData')}
                    </Typography>
                  )}
                </Grid>
              )}
            </Box>
          )}

          {/* ── Report tab ── */}
          {step === 1 && (
            <Box>
              {/* Report toolbar */}
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 3
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    onChange={(fromDate: any) => handleDateChange(fromDate, 5)}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        size="small"
                        sx={{ width: 160 }}
                      />
                    )}
                    value={reportRanges.fromDate}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    minDate={dayjs(reportRanges.fromDate) || undefined}
                    onChange={(toDate) => handleDateChange(toDate, 6)}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        size="small"
                        sx={{ width: 160 }}
                      />
                    )}
                    value={reportRanges.toDate}
                  />
                </LocalizationProvider>

                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="timesheet-report-view-toggle"
                    name="timesheet-report-view-toggle"
                    onChange={() => setReportByDate(!reportByDate)}
                  >
                    <FormControlLabel
                      checked={reportByDate}
                      control={<CustomRadio />}
                      label={t('TIMESHEET.byDate')}
                    />
                    <FormControlLabel
                      checked={!reportByDate}
                      control={<CustomRadio />}
                      label={t('TIMESHEET.byTechnician')}
                    />
                  </RadioGroup>
                </FormControl>

                <Button
                  onClick={() => downloadCSV()}
                  size="small"
                  sx={{
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    ml: 'auto',
                    px: '16px',
                    textTransform: 'none'
                  }}
                  variant="contained"
                >
                  {t('TIMESHEET.exportCSV')}
                </Button>
              </Box>

              {reportData?.length > 0 ? (
                <TimesheetReportTable
                  byDate={reportByDate}
                  data={reportData || []}
                  isTechnician={false}
                />
              ) : (
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 8 }}
                >
                  {isLoading ? (
                    <ViewLoader />
                  ) : (
                    <Typography
                      sx={{ color: secondaryColors.SECONDARY, fontSize: 13 }}
                    >
                      {t('GENERAL.noData')}
                    </Typography>
                  )}
                </Grid>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Timesheet;
